import { useCallback, useEffect, useMemo, useState } from "react";
import type { CheckedState } from "@radix-ui/react-checkbox";
import type { ICheckboxCardProps } from "@tenant/ui";

export const useCheckboxCard = (props: ICheckboxCardProps) => {
  const { checked, defaultChecked, disabled } = props;
  // For some reason, directly destructing `onCheckedChange` from `props`
  // causes a type error in some context when using the function. This is a
  // workaround.
  const onCheckedChange = props.onCheckedChange;
  // Allows component to be used in an uncontrolled way
  const [isChecked, setIsChecked] = useState(defaultChecked ?? false);

  /**
   * Passed to Radix so it needs to handle their checked type but drops the
   * "indeterminate" state for this component
   * */
  const setIsCheckedBoolean = useCallback((nextIsChecked: CheckedState) => {
    // coalesce `"indeterminate"` to `true`
    const boolIsChecked = nextIsChecked !== false;

    setIsChecked(boolIsChecked);
  }, []);

  const toggleIsChecked = useCallback(() => {
    setIsChecked(!isChecked);
    onCheckedChange?.(!isChecked);
  }, [isChecked, onCheckedChange]);

  // If using component in a controlled way, keep internal state in sync
  useEffect(() => {
    const boolIsChecked = checked ?? false;

    if (checked !== isChecked) {
      setIsChecked(boolIsChecked);
      onCheckedChange?.(boolIsChecked);
    }
  }, [isChecked, checked, onCheckedChange]);

  return useMemo(
    () => ({
      context: {
        isChecked,
        isDisabled: disabled ?? false,
      },
      handlers: {
        setIsCheckedBoolean,
        toggleIsChecked,
      },
    }),
    [disabled, isChecked, setIsCheckedBoolean, toggleIsChecked]
  );
};

export type ICheckboxCardData = ReturnType<typeof useCheckboxCard>;
